import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LuDownload, LuFileSearch } from 'react-icons/lu';
import { LoadingSpinner } from 'components/common';
import { getComparison } from 'components/app/document-compare/documentCompareApi';
import { RUNNING, errorOf, standardLabel, when } from 'components/app/document-compare/compareFormat';
import { Notice } from 'components/app/document-compare/ComparisonResult';
import FindingList from 'components/app/document-compare/FindingList';
import ChangeSummary from 'components/app/document-compare/viewer/ChangeSummary';
import DocumentViewer from 'components/app/document-compare/viewer/DocumentViewer';
import useComparisonPolling from 'components/app/document-compare/useComparisonPolling';

// One comparison online: the summary of changes and the findings beside the
// marked-up file itself (PDF or Word); a finding or a clause scrolls the file to it.
const ComparisonViewerPage = () => {
  const { id } = useParams();
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(null);

  useEffect(() => {
    getComparison(id).then((res) => setComparison(res.data)).catch((e) => setError(errorOf(e, 'Could not open this comparison.')));
  }, [id]);
  useComparisonPolling({ active: true, current: comparison, onUpdate: setComparison, onError: setError });

  const select = useCallback((finding) => {
    setTarget((old) => ({ finding, nonce: (old?.nonce || 0) + 1 }));
    document.getElementById(`finding-${finding.id}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, []);

  if (!comparison) {
    return (
      <div className="tw-p-4">
        {error ? <Notice tone="red">{error}</Notice> : <div className="tw-py-16 tw-flex tw-justify-center"><LoadingSpinner /></div>}
      </div>
    );
  }

  const running = RUNNING.includes(comparison.status);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <header className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <LuFileSearch className="tw-text-blue-600 tw-shrink-0" size={22} aria-hidden="true" />
        <div className="tw-min-w-0 tw-flex-1">
          <h1 className="tw-mb-0 tw-font-bold tw-text-xl tw-text-gray-900 tw-break-all">{comparison.sales_filename}</h1>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0">
            vs {standardLabel(comparison.template)} · {when(comparison.created_at)}
            {comparison.created_by ? ` · ${comparison.created_by}` : ''}
          </p>
        </div>
        {comparison.annotated_url && (
          <a href={comparison.annotated_url} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-primary-600 tw-text-white tw-text-[13px] tw-font-semibold hover:tw-bg-primary-700 tw-no-underline">
            <LuDownload size={14} aria-hidden="true" /> Download {comparison.annotated_filename?.split('.').pop()?.toUpperCase()}
          </a>
        )}
      </header>

      {error && <Notice tone="red">{error}</Notice>}
      {running && (
        <div role="status" className="tw-flex tw-items-center tw-gap-2 tw-py-10 tw-justify-center tw-text-sm tw-text-slate-600">
          <LoadingSpinner size="sm" /> Comparing… a scanned PDF can take a few minutes.
        </div>
      )}
      {comparison.status === 'failed' && <Notice tone="red">{comparison.error || 'The comparison failed.'}</Notice>}

      {comparison.status === 'done' && (
        <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-[420px_minmax(0,1fr)] tw-gap-3 tw-items-start">
          <aside className="tw-flex tw-flex-col tw-gap-4 tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-p-4 xl:tw-max-h-[calc(100vh-170px)] xl:tw-overflow-auto">
            <ChangeSummary comparison={comparison} onSelect={select} />
            <div className="tw-border-0 tw-border-t tw-border-solid tw-border-slate-200 tw-pt-3">
              <FindingList findings={comparison.findings || []} onSelect={select} selectedId={target?.finding.id} />
            </div>
          </aside>
          <div className="tw-h-[75vh] xl:tw-h-[calc(100vh-170px)]">
            {comparison.annotated_filename
              ? <DocumentViewer comparison={comparison} target={target} />
              : <Notice tone="amber">The marked-up file could not be made{comparison.stats?.annotate_error ? `: ${comparison.stats.annotate_error}` : '.'}</Notice>}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonViewerPage;
